import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_RECEPTION_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useBusinessCore } from '../../../../core/modules/business';
import moment from 'moment';
import { DOCUMENT_TYPES } from '../../../../core/constants/documentTypes';
import { useMasterCore } from '../../../../core/modules/master';
import { isEmpty } from 'lodash';
import { ERROR } from '../../../../core/constants/errorCode';
import { ReceptionOrder } from '../../../../core/interfaces/receptionOrder.interface';

const schema = yup.object().shape({
  received_at: yup.string().required('Campo requerido'),
  quantity: yup
    .number()
    .required('Campo requerido')
    .typeError('Formato incorrecto'),
  document_number: yup.string().optional(),
  document_type_id: yup.string().optional(),
  reception_order_id: yup.number().nullable().optional(),
  reception_type_id: yup.string().required('Campo requerido'),
  notes: yup.string().optional(),
});

interface Props {
  order: ReceptionOrder | null;
  onSuccess: () => void;
  onClose: () => void;
}

const CreateDialog = (props: Props) => {
  const { order, onSuccess, onClose } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { receptionOrders, postReception, getReceptions, errorCode } = useReceptionCore();
  const { companyId } = useCompanyCore();
  const { receptionTypes } = useMasterCore();
  const { businesses, getBusinesses } = useBusinessCore();

  const [loading, setLoading] = useState(false);

  const { control, formState, handleSubmit, reset, setValue } = useForm({
    mode: 'onChange',
    defaultValues: {
      received_at: moment().format('yyyy-MM-DD'),
    },
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  useEffect(() => {
    if (companyId && businesses.length === 0) {
      getBusinesses(companyId);
    }
  }, [companyId, businesses.length, getBusinesses]);

  useEffect(() => {
    if (order) {
      setValue('reception_order_id', order.id);
    } else {
      setValue('reception_order_id', null);
    }
  }, [order, setValue]);

  const availableOrders = useMemo(() => {
    const pendingOrders = receptionOrders.filter((receptionOrder) => !receptionOrder.has_receptions);

    if (order && !pendingOrders.some((pending) => pending.id === order.id)) {
      return [...pendingOrders, order];
    }

    return pendingOrders;
  }, [receptionOrders, order]);

  const orderOptions = useMemo(() => {
    const options = availableOrders.map((receptionOrder) => {
      const provider = businesses.find((business) => business.id === receptionOrder.business_id);
      const providerArea = provider?.areas?.find((area) => area.id === receptionOrder.business_area_id);

      const providerName = provider?.name || 'Sin proveedor';
      const areaAcronym = providerArea?.acronym || '';

      const labelParts = [`#${receptionOrder.id}`, providerName];
      if (areaAcronym) {
        labelParts.push(areaAcronym);
      }

      return {
        ...receptionOrder,
        providerName,
        areaAcronym,
        label: labelParts.join(' - '),
      };
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [availableOrders, businesses]);

  const selectedOrderLabel = useMemo(() => {
    if (!order) {
      return '';
    }

    const provider = businesses.find((business) => business.id === order.business_id);
    const providerArea = provider?.areas?.find((area) => area.id === order.business_area_id);

    const providerName = provider?.name || 'Sin proveedor';
    const areaAcronym = providerArea?.acronym || '';

    const labelParts = [`#${order.id}`, providerName];
    if (areaAcronym) {
      labelParts.push(areaAcronym);
    }

    return labelParts.join(' - ');
  }, [order, businesses]);

  const resetForm = useCallback(() => {
    reset({});
    onClose();
    closeDialog(CREATE_RECEPTION_DIALOG);
  }, [closeDialog, onClose, reset]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        
        // Preparar los datos para enviar - solo campos requeridos
        const payload: any = {
          received_at: moment(values.received_at).unix(),
          quantity: Number(values.quantity),
          reception_type_id: Number(values.reception_type_id),
        };
        
        // Agregar campos opcionales solo si tienen valor
        if (values.document_type_id && values.document_type_id !== '' && values.document_type_id !== null) {
          payload.document_type_id = Number(values.document_type_id);
        }
        
        // document_number: solo incluir si tiene valor
        const documentNumber = values.document_number?.trim();
        if (documentNumber && documentNumber !== '') {
          payload.document_number = documentNumber;
        }
        
        // notes: solo incluir si tiene valor
        const notes = values.notes?.trim();
        if (notes && notes !== '') {
          payload.notes = notes;
        }
        
        // reception_order_id: solo incluir si tiene valor
        if (order) {
          payload.reception_order_id = order.id;
        } else if (values.reception_order_id && values.reception_order_id !== '' && values.reception_order_id !== null) {
          payload.reception_order_id = Number(values.reception_order_id);
        }
        
        console.log('Payload a enviar:', JSON.stringify(payload, null, 2));
        
        postReception(
          companyId,
          payload,
          () => {
            resetForm();
            onSuccess();
            getReceptions(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, postReception, resetForm, onSuccess, getReceptions, order]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(CREATE_RECEPTION_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <Typography className="text-2xl font-bold truncate mb-16">
            Generar recepción
          </Typography>
          
          <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col space-y-24"
          >
            <Controller
              name="received_at"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fecha de Recepción"
                  type="date"
                  variant="outlined"
                  fullWidth
                  required
                />
              )}
            />

            {order ? (
              <TextField
                label="Orden de ingreso"
                value={selectedOrderLabel}
                variant="outlined"
                fullWidth
                disabled
              />
            ) : (
              <Controller
                name="reception_order_id"
                control={control}
                render={({ field }) => {
                  const fieldValue = field.value as unknown;
                  const rawValue =
                    fieldValue === undefined ||
                    fieldValue === null ||
                    (typeof fieldValue === 'string' && fieldValue.trim() === '')
                      ? null
                      : Number(fieldValue);
                  const selectedOption =
                    orderOptions.find((option) => option.id === rawValue) || null;

                  return (
                    <Autocomplete
                      options={orderOptions}
                      getOptionLabel={(option) => option.label}
                      value={selectedOption}
                      onChange={(_, newValue) => field.onChange(newValue ? newValue.id : null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Orden de ingreso (opcional)"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div className="flex flex-col">
                            <span>{option.label}</span>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            )}
                          </div>
                        </li>
                      )}
                      noOptionsText="No hay órdenes disponibles"
                      isOptionEqualToValue={(option, value) => value ? option.id === value.id : false}
                      disabled={loading || orderOptions.length === 0}
                      clearOnEscape
                      onBlur={field.onBlur}
                    />
                  );
                }}
              />
            )}

            {order && (
              <Typography variant="body2" color="textSecondary" className="mb-16">
                Orden de ingreso pre-seleccionada: {selectedOrderLabel}
              </Typography>
            )}

            <Controller
              name="reception_type_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel id="type-label">
                    Tipo de recepción
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="type-label"
                    label="Tipo de recepción"
                    disabled={loading}
                    required
                  >
                    {receptionTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="document_type_id"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="document_type-label">
                    Tipo de documento (Opcional)
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="document_type-label"
                    id="document_type-label"
                    label="Tipo de documento (Opcional)"
                    disabled={loading}
                  >
                    <MenuItem value="">
                      <em>Ninguno</em>
                    </MenuItem>
                    {DOCUMENT_TYPES.map(({ name, value }) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="document_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número de Documento (Opcional)"
                  variant="outlined"
                  fullWidth
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />

            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Cantidad"
                  type="number"
                  variant="outlined"
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  label="Observación (Opcional)"
                  variant="outlined"
                  fullWidth
                />
              )}
            />

            {errorCode === ERROR.ERR_409 && (
              <Typography color="error" className="text-center mb-32 text-md">
                Ya existe una recepción para esa orden
              </Typography>
            )}

            <Button
              variant="contained"
              color="secondary"
              disabled={!isEmpty(errors) || !isValid || loading}
              type="submit"
              size="large"
            >
              <Spinner show={loading} /> Generar
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
